import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const AccountsWorkOrders = ({ onNavigate, userInfo }) => {

  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountsData, setAccountsData] = useState({
    name: "Loading...",
    accountsId: "...",
    department: "Accounts Department",
    designation: "Accounts Officer",
    profileImage: null
  });

  // Work Orders Data States
  const [workOrdersData, setWorkOrdersData] = useState({
    totalWorkOrders: 0,
    activeWorkOrders: 0,
    completedWorkOrders: 0,
    pendingPayments: 0,
    totalOrderValue: 0,
    totalPaidAmount: 0,
    workOrders: []
  });

  // Filter and Search States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // NEW: PDF Generation and Add Work Order States
  const [showAddModal, setShowAddModal] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedWorkOrderForStatus, setSelectedWorkOrderForStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusRemarks, setStatusRemarks] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [newWorkOrder, setNewWorkOrder] = useState({
    workOrderNumber: '',
    workOrderDate: new Date().toISOString().split('T')[0],
    vendorName: '',
    vendorContact: '',
    vendorEmail: '',
    vendorGstin: '',
    workTitle: '',
    workDescription: '',
    workLocation: '',
    department: '',
    projectCode: '',
    estimatedValue: '',
    startDate: '',
    expectedCompletionDate: '',
    paymentTerms: '',
    warrantyPeriod: '',
    priority: 'medium',
    assignedSupervisor: '',
    status: 'draft'
  });

  // Check if mobile on mount and resize


  // Fetch Work Orders Data from API
  useEffect(() => {
    const fetchWorkOrdersData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          setError('Authentication required. Please login again.');
          setAccountsData(prev => ({
            ...prev,
            name: userInfo?.name || "Accounts User",
            accountsId: userInfo?.id || "ACC2024001"
          }));
          setLoading(false);
          return;
        }

        const apiUrl = `${API_LINK}/api/accounts/work-orders`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleLogout();
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.data) {
          const data = result.data;

          setAccountsData({
            name: userInfo?.name || "Accounts Officer",
            accountsId: userInfo?.id || "ACC2024001",
            department: "Accounts Department",
            designation: "Accounts Officer",
            profileImage: null
          });

          setWorkOrdersData({
            totalWorkOrders: data.stats?.totalWorkOrders || 0,
            activeWorkOrders: data.stats?.activeWorkOrders || 0,
            completedWorkOrders: data.stats?.completedWorkOrders || 0,
            pendingPayments: data.stats?.pendingPayments || 0,
            totalOrderValue: data.stats?.totalOrderValue || 0,
            totalPaidAmount: data.stats?.totalPaidAmount || 0,
            workOrders: data.workOrders || []
          });
        }

      } catch (error) {
        console.error('Error fetching work orders:', error);
        setError(`Failed to load work orders: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchWorkOrdersData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };



  // NEW: Handle Generate PDF for Work Order
  const handleGeneratePDF = async (workOrderId, workOrderNumber) => {
    try {
      console.log('📄 Generating PDF for work order:', workOrderId);

      setGeneratingPDF(prev => ({ ...prev, [workOrderId]: true }));

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(
        `${API_LINK}/api/accounts/work-orders/${workOrderId}/generate-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        console.log('✅ PDF generated successfully:', result.data);

        // Auto-download the generated PDF
        await handleDownloadPDF(workOrderId, workOrderNumber);

        // Refresh work orders list
        const ordersResponse = await fetch(
          `${API_LINK}/api/accounts/work-orders`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const ordersResult = await ordersResponse.json();

        if (ordersResult.success && ordersResult.data) {
          setWorkOrdersData({
            totalWorkOrders: ordersResult.data.stats?.totalWorkOrders || 0,
            activeWorkOrders: ordersResult.data.stats?.activeWorkOrders || 0,
            completedWorkOrders: ordersResult.data.stats?.completedWorkOrders || 0,
            pendingPayments: ordersResult.data.stats?.pendingPayments || 0,
            totalOrderValue: ordersResult.data.stats?.totalOrderValue || 0,
            totalPaidAmount: ordersResult.data.stats?.totalPaidAmount || 0,
            workOrders: ordersResult.data.workOrders || []
          });
        }

        alert(`Work Order PDF generated and downloaded!\nOrder: ${result.data.workOrderNumber}`);
      } else {
        alert(`Failed to generate PDF: ${result.message}`);
      }

    } catch (error) {
      console.error('💥 Error generating PDF:', error);
      alert(`Error generating PDF: ${error.message}`);
    } finally {
      setGeneratingPDF(prev => ({ ...prev, [workOrderId]: false }));
    }
  };

  // NEW: Handle Download PDF
  const handleDownloadPDF = (workOrderId, workOrderNumber) => {
    console.log('📥 Downloading PDF for work order:', workOrderId);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    const downloadUrl = `${API_LINK}/api/accounts/work-orders/${workOrderId}/download-pdf`;

    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `WorkOrder_${workOrderNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        console.log('✅ PDF download initiated');
      })
      .catch(error => {
        console.error('❌ Error downloading PDF:', error);
        alert('Error downloading PDF. Please try again.');
      });
  };

  // NEW: Handle Add Work Order Form Submit
  const handleAddWorkOrder = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(
        `${API_LINK}/api/accounts/work-orders`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newWorkOrder)
        }
      );

      const result = await response.json();

      if (result.success) {
        alert(`Work Order created successfully!\nOrder Number: ${newWorkOrder.workOrderNumber}`);
        setShowAddModal(false);

        // Reset form
        setNewWorkOrder({
          workOrderNumber: '',
          workOrderDate: new Date().toISOString().split('T')[0],
          vendorName: '',
          vendorContact: '',
          vendorEmail: '',
          vendorGstin: '',
          workTitle: '',
          workDescription: '',
          workLocation: '',
          department: '',
          projectCode: '',
          estimatedValue: '',
          startDate: '',
          expectedCompletionDate: '',
          paymentTerms: '',
          warrantyPeriod: '',
          priority: 'medium',
          assignedSupervisor: '',
          status: 'draft'
        });

        // Refresh work orders list
        window.location.reload();
      } else {
        alert(`Failed to create work order: ${result.message}`);
      }

    } catch (error) {
      console.error('💥 Error creating work order:', error);
      alert(`Error creating work order: ${error.message}`);
    }
  };

  // NEW: Handle Status Change
  const handleOpenStatusModal = (order) => {
    setSelectedWorkOrderForStatus(order);
    setNewStatus(order.status);
    setStatusRemarks('');
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedWorkOrderForStatus) return;

    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const response = await fetch(
        `${API_LINK}/api/accounts/work-orders/${selectedWorkOrderForStatus.id}/status`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: newStatus,
            remarks: statusRemarks || null
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        alert(`Status updated successfully!\nWork Order: ${result.data.workOrderNumber}\nNew Status: ${newStatus.toUpperCase().replace(/-/g, ' ')}`);
        setShowStatusModal(false);

        // Refresh work orders list
        window.location.reload();
      } else {
        alert(`Failed to update status: ${result.message}`);
      }

    } catch (error) {
      console.error('💥 Error updating status:', error);
      alert(`Error updating status: ${error.message}`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Handle view details
  const handleViewDetails = (order) => {
    setSelectedWorkOrder(order);
    setShowDetailsModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return `₹${parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'draft': '#9e9e9e',
      'pending-approval': '#ff9800',
      'approved': '#2196f3',
      'in-progress': '#238548',
      'completed': '#4caf50',
      'on-hold': '#f44336',
      'cancelled': '#000000',
      'closed': '#607d8b'
    };
    return colors[status] || '#9e9e9e';
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      'low': '#4caf50',
      'medium': '#ff9800',
      'high': '#f44336',
      'urgent': '#d32f2f'
    };
    return colors[priority] || '#ff9800';
  };



  // Filter work orders
  const filteredWorkOrders = workOrdersData.workOrders.filter(order => {
    const matchesSearch = searchTerm === '' ||
      order.work_order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.work_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.vendor_contractor_name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Work Orders...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '3rem', color: '#f44336' }}>⚠️</div>
        <p style={{ color: '#666', fontSize: '1.1rem', textAlign: 'center', maxWidth: '400px' }}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#238548',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Layout role="accounts"
      onNavigate={onNavigate}
      activePage="accounts-work-orders"
      pageTitle="Work Orders Management"
      accountsData={accountsData}
    >


      {/* Page Header with Add Button */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{
            margin: '0 0 1rem 0',
            color: '#333',
            fontSize: '2rem',
            fontWeight: 'bold'
          }}>
            📋 Work Orders Management
          </h1>
          <p style={{
            margin: 0,
            color: '#666',
            fontSize: '1.1rem',
            lineHeight: 1.6
          }}>
            Manage work orders, track vendor assignments, and monitor project progress.
          </p>
        </div>

        {/* Add Work Order Button */}
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#238548',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 2px 8px rgba(35, 133, 72, 0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#1a6335';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#238548';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          ➕ Add Work Order
        </button>
      </div>

      {/* Stats Cards */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {[
          {
            icon: '📋',
            label: 'Total Work Orders',
            value: workOrdersData.totalWorkOrders,
            color: '#238548'
          },
          {
            icon: '⚡',
            label: 'Active Orders',
            value: workOrdersData.activeWorkOrders,
            color: '#2196f3'
          },
          {
            icon: '✅',
            label: 'Completed',
            value: workOrdersData.completedWorkOrders,
            color: '#4caf50'
          },
          {
            icon: '💰',
            label: 'Pending Payments',
            value: workOrdersData.pendingPayments,
            color: '#ff9800'
          },
          {
            icon: '💵',
            label: 'Total Order Value',
            value: formatCurrency(workOrdersData.totalOrderValue),
            color: '#238548'
          },
          {
            icon: '✔️',
            label: 'Total Paid',
            value: formatCurrency(workOrdersData.totalPaidAmount),
            color: '#4caf50'
          }
        ].map((stat, index) => (
          <div key={index} style={{
            background: '#ffffff',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>{stat.icon}</div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#666', fontSize: '0.9rem', fontWeight: 'normal' }}>
              {stat.label}
            </h3>
            <p style={{
              fontSize: '1.8rem',
              fontWeight: 'bold',
              color: stat.color,
              margin: 0
            }}>
              {stat.value}
            </p>
          </div>
        ))}
      </section>

      {/* Filters and Search */}
      <section style={{
        background: '#ffffff',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
          gap: '1rem'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666', fontWeight: '500' }}>
              Search Work Orders
            </label>
            <input
              type="text"
              placeholder="Search by order number, title, or vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#666', fontWeight: '500' }}>
              Filter by Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending-approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </section>

      {/* Work Orders Table */}
      <section style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <span style={{ fontSize: '1.8rem' }}>📋</span>
          <h3 style={{ margin: 0, fontSize: '1.2rem' }}>
            Work Orders ({filteredWorkOrders.length})
          </h3>
        </div>

        <div style={{ padding: '1.5rem', overflowX: 'auto' }}>
          {filteredWorkOrders.length > 0 ? (
            <div style={{ minWidth: '1000px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #238548' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#333', fontWeight: 'bold' }}>
                      Order Number
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#333', fontWeight: 'bold' }}>
                      Work Title
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#333', fontWeight: 'bold' }}>
                      Vendor
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#333', fontWeight: 'bold' }}>
                      Value
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#333', fontWeight: 'bold' }}>
                      Status
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'left', color: '#333', fontWeight: 'bold' }}>
                      Priority
                    </th>
                    <th style={{ padding: '1rem', textAlign: 'center', color: '#333', fontWeight: 'bold' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWorkOrders.map((order, index) => {
                    return (
                      <tr key={index} style={{
                        borderBottom: '1px solid #ecf0f1',
                        transition: 'background 0.2s ease'
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '1rem', color: '#333', fontWeight: '500' }}>
                          {order.work_order_number}
                        </td>
                        <td style={{ padding: '1rem', color: '#666' }}>
                          {order.work_title}
                        </td>
                        <td style={{ padding: '1rem', color: '#666' }}>
                          {order.vendor_contractor_name}
                        </td>
                        <td style={{ padding: '1rem', color: '#238548', fontWeight: 'bold' }}>
                          {formatCurrency(order.estimated_value)}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            backgroundColor: `${getStatusColor(order.status)}20`,
                            color: getStatusColor(order.status),
                            textTransform: 'capitalize',
                            whiteSpace: 'nowrap'
                          }}>
                            {order.status.replace(/-/g, ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            backgroundColor: `${getPriorityColor(order.priority)}20`,
                            color: getPriorityColor(order.priority),
                            textTransform: 'capitalize'
                          }}>
                            {order.priority}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {/* Change Status Button */}
                            <button
                              onClick={() => handleOpenStatusModal(order)}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#2196f3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              🔄 Change Status
                            </button>

                            {/* Generate PDF Button */}
                            {!order.work_order_file && (
                              <button
                                onClick={() => handleGeneratePDF(order.id, order.work_order_number)}
                                disabled={generatingPDF[order.id]}
                                style={{
                                  padding: '0.5rem 1rem',
                                  backgroundColor: generatingPDF[order.id] ? '#cccccc' : '#ff9800',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: generatingPDF[order.id] ? 'not-allowed' : 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {generatingPDF[order.id] ? '⏳ Generating...' : '📄 Generate PDF'}
                              </button>
                            )}

                            {/* Download PDF Button */}
                            {order.work_order_file && (
                              <button
                                onClick={() => handleDownloadPDF(order.id, order.work_order_number)}
                                style={{
                                  padding: '0.5rem 1rem',
                                  backgroundColor: '#4caf50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                📥 Download PDF
                              </button>
                            )}

                            <button
                              onClick={() => handleViewDetails(order)}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#238548',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              View Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '3rem 2rem',
              color: '#666'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
              <p style={{ fontSize: '1.1rem', margin: 0 }}>
                {searchTerm || statusFilter !== 'all'
                  ? 'No work orders found matching your filters'
                  : 'No work orders available'}
              </p>
            </div>
          )}
        </div>
      </section>


      {/* Change Status Modal */}
      {
        showStatusModal && selectedWorkOrderForStatus && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '1rem'
            }}
            onClick={() => setShowStatusModal(false)}
          >
            <div
              style={{
                background: '#ffffff',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '100%',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                background: '#2196f3',
                color: '#ffffff',
                padding: '1.5rem 2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem' }}>🔄 Change Work Order Status</h2>
                <button
                  onClick={() => setShowStatusModal(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '8px'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: '2rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}>
                    <strong>Work Order:</strong> {selectedWorkOrderForStatus.work_order_number}
                  </p>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}>
                    <strong>Title:</strong> {selectedWorkOrderForStatus.work_title}
                  </p>
                  <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
                    <strong>Current Status:</strong>{' '}
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      backgroundColor: `${getStatusColor(selectedWorkOrderForStatus.status)}20`,
                      color: getStatusColor(selectedWorkOrderForStatus.status),
                      textTransform: 'capitalize'
                    }}>
                      {selectedWorkOrderForStatus.status.replace(/-/g, ' ')}
                    </span>
                  </p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                    New Status <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="draft">Draft</option>
                    <option value="pending-approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on-hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                    Remarks (Optional)
                  </label>
                  <textarea
                    value={statusRemarks}
                    onChange={(e) => setStatusRemarks(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      minHeight: '80px',
                      resize: 'vertical'
                    }}
                    placeholder="Add remarks about this status change..."
                  />
                </div>

                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    disabled={updatingStatus}
                    style={{
                      padding: '0.75rem 2rem',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: updatingStatus ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500',
                      opacity: updatingStatus ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateStatus}
                    disabled={updatingStatus}
                    style={{
                      padding: '0.75rem 2rem',
                      backgroundColor: updatingStatus ? '#cccccc' : '#2196f3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: updatingStatus ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500'
                    }}
                  >
                    {updatingStatus ? '⏳ Updating...' : '✅ Update Status'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Add Work Order Modal */}
      {
        showAddModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '1rem',
              overflowY: 'auto'
            }}
            onClick={() => setShowAddModal(false)}
          >
            <div
              style={{
                background: '#ffffff',
                borderRadius: '12px',
                maxWidth: '900px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                margin: '2rem 0'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                background: '#238548',
                color: '#ffffff',
                padding: '1.5rem 2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
                position: 'sticky',
                top: 0,
                zIndex: 1
              }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem' }}>➕ Add New Work Order</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '8px'
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleAddWorkOrder} style={{ padding: '2rem' }}>
                {/* Work Order Basic Info */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#238548', fontSize: '1.2rem' }}>
                    📋 Work Order Information
                  </h3>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '1rem'
                  }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Work Order Number <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={newWorkOrder.workOrderNumber}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, workOrderNumber: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="WO-2024-001"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Order Date <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={newWorkOrder.workOrderDate}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, workOrderDate: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Vendor Details */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#238548', fontSize: '1.2rem' }}>
                    🏢 Vendor/Contractor Details
                  </h3>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '1rem'
                  }}>
                    <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Vendor/Contractor Name <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={newWorkOrder.vendorName}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, vendorName: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="ABC Contractors Pvt Ltd"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Contact Number <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={newWorkOrder.vendorContact}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, vendorContact: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="9876543210"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={newWorkOrder.vendorEmail}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, vendorEmail: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="vendor@example.com"
                      />
                    </div>

                    <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        GSTIN
                      </label>
                      <input
                        type="text"
                        value={newWorkOrder.vendorGstin}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, vendorGstin: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="22AAAAA0000A1Z5"
                      />
                    </div>
                  </div>
                </div>

                {/* Work Details */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#238548', fontSize: '1.2rem' }}>
                    🔨 Work Details
                  </h3>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                      Work Title <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={newWorkOrder.workTitle}
                      onChange={(e) => setNewWorkOrder({ ...newWorkOrder, workTitle: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                      placeholder="Building Renovation Work"
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                      Work Description <span style={{ color: 'red' }}>*</span>
                    </label>
                    <textarea
                      required
                      value={newWorkOrder.workDescription}
                      onChange={(e) => setNewWorkOrder({ ...newWorkOrder, workDescription: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        minHeight: '100px',
                        resize: 'vertical'
                      }}
                      placeholder="Detailed description of the work to be performed..."
                    />
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '1rem'
                  }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Work Location <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={newWorkOrder.workLocation}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, workLocation: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="Main Campus Building"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Department <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={newWorkOrder.department}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, department: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="Maintenance"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Project Code
                      </label>
                      <input
                        type="text"
                        value={newWorkOrder.projectCode}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, projectCode: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="PROJ-2024-001"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Estimated Value (₹) <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={newWorkOrder.estimatedValue}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, estimatedValue: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="500000"
                      />
                    </div>
                  </div>
                </div>

                {/* Timeline & Terms */}
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#238548', fontSize: '1.2rem' }}>
                    📅 Timeline & Terms
                  </h3>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '1rem'
                  }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Start Date <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={newWorkOrder.startDate}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, startDate: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Expected Completion <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={newWorkOrder.expectedCompletionDate}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, expectedCompletionDate: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Priority
                      </label>
                      <select
                        value={newWorkOrder.priority}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, priority: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Warranty Period
                      </label>
                      <input
                        type="text"
                        value={newWorkOrder.warrantyPeriod}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, warrantyPeriod: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="12 months"
                      />
                    </div>

                    <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Payment Terms
                      </label>
                      <textarea
                        value={newWorkOrder.paymentTerms}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, paymentTerms: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          minHeight: '60px',
                          resize: 'vertical'
                        }}
                        placeholder="Payment terms and conditions..."
                      />
                    </div>

                    <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#333', fontWeight: '500' }}>
                        Assigned Supervisor
                      </label>
                      <input
                        type="text"
                        value={newWorkOrder.assignedSupervisor}
                        onChange={(e) => setNewWorkOrder({ ...newWorkOrder, assignedSupervisor: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                        placeholder="Supervisor Name"
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  marginTop: '2rem',
                  paddingTop: '1.5rem',
                  borderTop: '1px solid #eee'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    style={{
                      padding: '0.75rem 2rem',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '0.75rem 2rem',
                      backgroundColor: '#238548',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500'
                    }}
                  >
                    ➕ Create Work Order
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Details Modal */}
      {
        showDetailsModal && selectedWorkOrder && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '1rem'
            }}
            onClick={() => setShowDetailsModal(false)}
          >
            <div
              style={{
                background: '#ffffff',
                borderRadius: '12px',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                background: '#238548',
                color: '#ffffff',
                padding: '1.5rem 2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Work Order Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '8px'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: '2rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1.3rem' }}>
                    {selectedWorkOrder.work_title}
                  </h3>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}>
                    <strong>Order Number:</strong> {selectedWorkOrder.work_order_number}
                  </p>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}>
                    <strong>Vendor:</strong> {selectedWorkOrder.vendor_contractor_name}
                  </p>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}>
                    <strong>Department:</strong> {selectedWorkOrder.department}
                  </p>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Order Date</div>
                    <div style={{ color: '#333', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatDate(selectedWorkOrder.work_order_date)}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Expected Completion</div>
                    <div style={{ color: '#333', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatDate(selectedWorkOrder.expected_completion_date)}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Estimated Value</div>
                    <div style={{ color: '#238548', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatCurrency(selectedWorkOrder.estimated_value)}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Final Value</div>
                    <div style={{ color: '#2e7d32', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {formatCurrency(selectedWorkOrder.final_value || selectedWorkOrder.estimated_value)}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Progress</div>
                    <div style={{ color: '#238548', fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {selectedWorkOrder.progress_percentage}%
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>Priority</div>
                    <div style={{ color: '#333', fontWeight: 'bold', fontSize: '1.1rem', textTransform: 'capitalize' }}>
                      {selectedWorkOrder.priority}
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#333' }}>Description</h4>
                  <p style={{ margin: 0, color: '#666', lineHeight: 1.6 }}>
                    {selectedWorkOrder.work_description}
                  </p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#333' }}>Work Location</h4>
                  <p style={{ margin: 0, color: '#666' }}>
                    {selectedWorkOrder.work_location || 'N/A'}
                  </p>
                </div>

                {selectedWorkOrder.payment_terms && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', color: '#333' }}>Payment Terms</h4>
                    <p style={{ margin: 0, color: '#666', lineHeight: 1.6 }}>
                      {selectedWorkOrder.payment_terms}
                    </p>
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  marginTop: '2rem'
                }}>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    style={{
                      padding: '0.75rem 2rem',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

    </Layout>
  );
};

export default AccountsWorkOrders;